import { DateUtils } from "services";

export const convertGradesToLetters = (data: any): any => {
  if (!data || typeof data !== "object") return data;

  const convertGrade = (grade: any): string | null => {
    if (!grade || grade === "N/A") return null;
    const str = String(grade);
    if (/^[A-F][+-]?$/i.test(str.trim())) return str.trim().toUpperCase();

    let num: number | null = null;

    const percentMatch = str.match(/(\d+(?:\.\d+)?)\s*%/);
    if (percentMatch) {
      num = parseFloat(percentMatch[1]);
      const gpa = num / 25;
      return convertGPAToLetter(gpa);
    }

    const cgpaMatch = str.match(
      /(?:CGPA|GPA):\s*(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/i
    );
    if (cgpaMatch) {
      const score = parseFloat(cgpaMatch[1]);
      const max = parseFloat(cgpaMatch[2]);
      const gpa = (score / max) * 4.0;
      return convertGPAToLetter(gpa);
    }

    const numMatch = str.match(/(\d+(?:\.\d+)?)/);
    if (numMatch) {
      num = parseFloat(numMatch[1]);

      let gpa: number;
      if (num > 20) {
        gpa = num / 25;
      } else if (num > 10) {
        gpa = (num / 10) * 4;
      } else if (num > 4) {
        gpa = (num / 5) * 4;
      } else {
        gpa = num;
      }

      return convertGPAToLetter(gpa);
    }

    return null;
  };

  const convertGPAToLetter = (gpa: number): string => {
    if (gpa >= 3.7) return "A";
    if (gpa >= 3.3) return "B+";
    if (gpa >= 3.0) return "B";
    if (gpa >= 2.7) return "B-";
    if (gpa >= 2.3) return "C+";
    if (gpa >= 2.0) return "C";
    if (gpa >= 1.0) return "D";
    return "F";
  };

  const converted = { ...data };

  if (converted.highSchool?.gradeObtained) {
    const grade = convertGrade(converted.highSchool.gradeObtained);
    if (grade) {
      converted.highSchool.gradeObtained = grade;
    } else {
      delete converted.highSchool.gradeObtained;
    }
  }

  for (let i = 1; i <= 3; i++) {
    const key = `tertiaryEducation${i}`;
    if (converted[key]?.gradeObtained) {
      const grade = convertGrade(converted[key].gradeObtained);
      if (grade) {
        converted[key].gradeObtained = grade;
      } else {
        delete converted[key].gradeObtained;
      }
    }
  }

  return converted;
};

export const getFieldImportance = (fieldName: string): number => {
  const field = fieldName.toLowerCase();

  // Basic Details - highest priority
  if (field.includes("title") && field.includes("basic")) return 105;
  if (field.includes("firstname")) return 100;
  if (field.includes("middlename")) return 99;
  if (field.includes("lastname")) return 98;
  if (field.includes("gender")) return 97;
  if (field.includes("dateofbirth")) return 96;
  if (field.includes("maritalstatus")) return 95;
  if (field.includes("registreddateofmarriage")) return 94;
  if (field.includes("countryofbirth")) return 93;
  if (field.includes("countryofcitizenship")) return 92;

  // Contact Details - Address
  if (field.includes("street1")) return 85;
  if (field.includes("street2")) return 84;
  if (field.includes("city")) return 83;
  if (field.includes("state")) return 82;
  if (field.includes("postalcode")) return 81;

  // Contact Details - Phone/Email
  if (field.includes("email")) return 75;
  if (field.includes("mobile") && field.includes("countrycode")) return 74;
  if (field.includes("mobile") && field.includes("number")) return 73;
  if (field.includes("phone") && field.includes("countrycode")) return 72;
  if (field.includes("phone") && field.includes("number")) return 71;

  // Emergency Contact
  if (field.includes("emergencycontactname")) return 70;
  if (field.includes("emergencycontactrelationship")) return 69;

  // Passport Details
  if (field.includes("passportnumber")) return 65;
  if (field.includes("passportexpirydate")) return 64;
  if (field.includes("passportissuingcountry")) return 63;

  // Visa Details
  if (field.includes("currentvisatype")) return 60;
  if (field.includes("visaexpirydate")) return 59;

  // Education - School/Institution first, then course, then dates
  if (field.includes("school") && !field.includes("high")) return 55;
  if (field.includes("course")) return 54;
  if (field.includes("startdate") || field.includes("datecommenced")) return 50;
  if (field.includes("enddate") || field.includes("dateended")) return 49;
  if (field.includes("gradeobtained")) return 48;

  // Employment
  if (field.includes("companyname")) return 45;
  if (field.includes("positionandduties")) return 44;

  // English Test
  if (field.includes("englishtest")) return 40;
  if (field.includes("overallscore")) return 39;
  if (field.includes("reading")) return 38;
  if (field.includes("writing")) return 37;
  if (field.includes("speaking")) return 36;
  if (field.includes("listening")) return 35;

  // OSHC
  if (field.includes("oshcprovidername")) return 30;
  if (field.includes("oshcmembershipnumber")) return 29;
  if (field.includes("oshcexpirydate")) return 28;

  // Other dates
  if (field.includes("date")) return 25;

  // Default
  return 5;
};

export const formatFieldName = (field: string): string => {
  const parts = field.split(".");
  const lastPart = parts[parts.length - 1];
  return lastPart
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
};

export const isDateField = (fieldPath: string): boolean => {
  const lowerFieldPath = fieldPath.toLowerCase();
  return (
    lowerFieldPath.includes("date") ||
    lowerFieldPath.includes("expiry") ||
    lowerFieldPath.endsWith("commenced") ||
    lowerFieldPath.endsWith("ended")
  );
};

export const mapDataByFileType = (
  extractedData: { [fileName: string]: any },
  uploadedResults: Array<{ fileName: string; fileType: string }>
): { [fileName: string]: any } => {
  const mappedData = { ...extractedData };

  uploadedResults.forEach(({ fileName, fileType }) => {
    const fileData = mappedData[fileName];
    if (!fileData) return;

    if (fileType === "employment1reference") {
      ["employment1", "employment2", "employment3"].forEach(
        (field: string, index: number) => {
          if (fileData[field] && index !== 0) {
            fileData.employment1 = fileData[field];
            delete fileData[field];
          }
        }
      );
    } else if (fileType === "employment2reference") {
      ["employment1", "employment2", "employment3"].forEach(
        (field: string, index: number) => {
          if (fileData[field] && index !== 1) {
            fileData.employment2 = fileData[field];
            delete fileData[field];
          }
        }
      );
    } else if (fileType === "employment3reference") {
      ["employment1", "employment2", "employment3"].forEach(
        (field: string, index: number) => {
          if (fileData[field] && index !== 2) {
            fileData.employment3 = fileData[field];
            delete fileData[field];
          }
        }
      );
    } else if (fileType === "tertiaryeducationcertificate1") {
      ["tertiaryEducation2", "tertiaryEducation3"].forEach((field: string) => {
        if (fileData[field]) {
          fileData.tertiaryEducation1 = fileData[field];
          delete fileData[field];
        }
      });
    } else if (fileType === "tertiaryeducationcertificate2") {
      ["tertiaryEducation1", "tertiaryEducation3"].forEach((field: string) => {
        if (fileData[field]) {
          fileData.tertiaryEducation2 = fileData[field];
          delete fileData[field];
        }
      });
    } else if (fileType === "tertiaryeducationcertificate3") {
      ["tertiaryEducation1", "tertiaryEducation2"].forEach((field: string) => {
        if (fileData[field]) {
          fileData.tertiaryEducation3 = fileData[field];
          delete fileData[field];
        }
      });
    } else if (fileType === "tertiarytranscript1") {
      ["tertiaryEducation2", "tertiaryEducation3"].forEach((field: string) => {
        if (fileData[field]) {
          fileData.tertiaryEducation1 = fileData[field];
          delete fileData[field];
        }
      });
    } else if (fileType === "tertiarytranscript2") {
      ["tertiaryEducation1", "tertiaryEducation3"].forEach((field: string) => {
        if (fileData[field]) {
          fileData.tertiaryEducation2 = fileData[field];
          delete fileData[field];
        }
      });
    } else if (fileType === "tertiarytranscript3") {
      ["tertiaryEducation1", "tertiaryEducation2"].forEach((field: string) => {
        if (fileData[field]) {
          fileData.tertiaryEducation3 = fileData[field];
          delete fileData[field];
        }
      });
    } else if (
      fileType === "highschoolcertificate" ||
      fileType === "highschooltranscript"
    ) {
      [
        "tertiaryEducation1",
        "tertiaryEducation2",
        "tertiaryEducation3",
      ].forEach((field: string) => {
        if (fileData[field]) {
          fileData.highSchool = fileData[field];
          delete fileData[field];
        }
      });
    } else if (fileType === "passport" || fileType === "additionalpassports") {
      if (fileData.passportDetails) {
        if (!fileData.personalInformation) fileData.personalInformation = {};
        fileData.personalInformation.passportDetails = fileData.passportDetails;
        delete fileData.passportDetails;
      }
    } else if (fileType === "visa") {
      if (fileData.visaDetails) {
        if (!fileData.personalInformation) fileData.personalInformation = {};
        fileData.personalInformation.visaDetails = fileData.visaDetails;
        delete fileData.visaDetails;
      }
    } else if (fileType === "marriagecertificate") {
      if (fileData.maritalStatus || fileData.registeredDateOfMarriage) {
        if (!fileData.personalInformation) fileData.personalInformation = {};
        if (fileData.maritalStatus) {
          fileData.personalInformation.maritalStatus = fileData.maritalStatus;
          delete fileData.maritalStatus;
        }
        if (fileData.registeredDateOfMarriage) {
          fileData.personalInformation.registeredDateOfMarriage =
            fileData.registeredDateOfMarriage;
          delete fileData.registeredDateOfMarriage;
        }
      }
    } else if (fileType === "englishproficiencycertificate") {
      ["ielts", "toefl", "pte", "englishTest"].forEach((field: string) => {
        if (fileData[field]) {
          fileData.englishLanguageTestScores = fileData[field];
          delete fileData[field];
        }
      });
    }
  });

  return mappedData;
};

export const consolidateExtractedData = (extractedData: {
  [fileName: string]: any;
}) => {
  const fieldOptions: {
    [field: string]: Array<{ value: any; source: string }>;
  } = {};
  const initialValues: { [field: string]: any } = {};
  const groupedFields: { [section: string]: string[] } = {};

  Object.entries(extractedData).forEach(([fileName, documentData]) => {
    const processObject = (
      obj: any,
      currentPath: any = initialValues,
      sectionKey = ""
    ) => {
      Object.entries(obj).forEach(([key, value]) => {
        if (
          value &&
          typeof value === "object" &&
          !Array.isArray(value) &&
          !(value instanceof Date)
        ) {
          if (!currentPath[key]) {
            currentPath[key] = {};
          }

          const newSectionKey = sectionKey ? `${sectionKey}.${key}` : key;
          processObject(value, currentPath[key], newSectionKey);
        } else if (value !== null && value !== undefined && value !== "") {
          let processedValue: any = value;
          const fullPath = sectionKey ? `${sectionKey}.${key}` : key;

          if (isDateField(fullPath)) {
            if (
              typeof value === "string" &&
              (value.toLowerCase().includes("present") ||
                value.toLowerCase().includes("current") ||
                value.toLowerCase().includes("continuing"))
            ) {
              processedValue = value;
            } else {
              processedValue = DateUtils.parseDate(
                value as string | Date | null | undefined
              );

              if (!processedValue) {
                return;
              }
            }
          }

          if (!fieldOptions[fullPath]) {
            fieldOptions[fullPath] = [];
          }

          const existingOption = fieldOptions[fullPath].find((option) => {
            if (isDateField(fullPath)) {
              return (
                option.value instanceof Date &&
                processedValue instanceof Date &&
                option.value.getTime() === processedValue.getTime()
              );
            }
            return String(option.value) === String(processedValue);
          });

          if (!existingOption) {
            fieldOptions[fullPath].push({
              value: processedValue,
              source: fileName,
            });
          }
          if (!currentPath.hasOwnProperty(key)) {
            currentPath[key] = processedValue;
          }

          const parts = fullPath.split(".");
          let section: string;

          if (
            fullPath === "personalInformation.maritalStatus" ||
            fullPath === "personalInformation.registeredDateOfMarriage"
          ) {
            section = "personalInformation.basicDetails";
          } else if (parts[0] === "personalInformation" && parts.length > 1) {
            section = `${parts[0]}.${parts[1]}`;
          } else {
            section = parts[0];
          }

          if (!groupedFields[section]) {
            groupedFields[section] = [];
          }
          if (!groupedFields[section].includes(fullPath)) {
            groupedFields[section].push(fullPath);
          }
        }
      });
    };

    processObject(documentData);
  });

  return { fieldOptions, initialValues, groupedFields };
};
